import { Inject, Injectable, PLATFORM_ID, Optional, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import {  isPlatformBrowser } from '@angular/common';
// Components
import { LoginComponent } from '../../modules/auth/login/login.component';
import { MatDialog } from '@angular/material/dialog';
// Constants
import { CheckToken, TokenInterface } from '@interfaces/auth/login.interface';
import { EndpointConstant } from '@interfaces/global/endpoint.constant';
import { UpdateSubscriptionTypesEnum } from '@interfaces/orders/enums/update-subscription-types.enum';
import { UsersInterface } from '@interfaces/components/users.interface';
// Services
import { RequestMethodsService } from '@services/request/request-methods.service';
import { GlobalSubscriptionService } from '@services/subscriptions/global-subscription.service';
import { BrowserService } from '@services/components/browser.service';
import { BasketService } from '@services/basket/basket.service';
import {SsrCookieService} from "ngx-cookie-service-ssr";
import {Request} from "express";

// Injection tokens for server request/response
export const REQUEST = new InjectionToken<any>('REQUEST');
export const RESPONSE = new InjectionToken<any>('RESPONSE');

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  user!: TokenInterface | null;
  tokenNameInLocalStorage = 'AuthTokenRetail';
  userInfoNameInLocalStorage = 'UserInfo';

  private isBrowser!: boolean;
  private request!: Request;

  constructor(
    private requestService: RequestMethodsService,
    private router: Router,
    private dialog: MatDialog,
    private globalSubscriptionService: GlobalSubscriptionService,
    private browserService: BrowserService,
    private basketService: BasketService,
    @Inject(PLATFORM_ID) private platformId: any,
    private ssrCoockieService: SsrCookieService,
    @Optional() @Inject('REQUEST') request: any
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.request = request;
    this.user = this.getTokenFromLocalStorage();
  }

  setToken(response: TokenInterface): void {
    this.user = response;
    this.ssrCoockieService.set(this.tokenNameInLocalStorage, JSON.stringify(response), {
      path: '/',
      sameSite: 'Strict'
    })
    this.getUserInfoAndSaveInLocalStorage();
  }

  getTokenFromLocalStorage(): TokenInterface | null {
    const user = this.isBrowser ? this.ssrCoockieService.get(this.tokenNameInLocalStorage) : this.request.cookies[this.tokenNameInLocalStorage];
    return user ? JSON.parse(user) : null;
  }

  getBadgesMessages(): Observable<any> {
    return  this.requestService.get(`${EndpointConstant.badges}/${EndpointConstant.chats}`)
  }

  getUser(): TokenInterface | null {
    if (this.user) {
      return this.user;
    }
    const storedToken = this.isBrowser ? this.ssrCoockieService.get(this.tokenNameInLocalStorage) : this.request.cookies[this.tokenNameInLocalStorage];
    if (storedToken) {
      this.user = JSON.parse(storedToken);
      return this.user;
    }
    return null;
  }

  getUserInfoAndSaveInLocalStorage(reloadPage = true): void {
    this.requestService.get(`${EndpointConstant.users}/${EndpointConstant.self}`).subscribe((res: UsersInterface) => {
      this.ssrCoockieService.set(this.userInfoNameInLocalStorage, JSON.stringify(res), {
        path: '/',
        sameSite: 'Strict'
      })
      if (this.isBrowser) {
        if (reloadPage) {
          window.location.href = '/';
          this.basketService.clearBasket().subscribe(() => {});
        } else {
          this.globalSubscriptionService.sendMessage(UpdateSubscriptionTypesEnum.UPDATE_PROFILE)
        }
      }

    })
  }


  getUserInfo(): UsersInterface | null {
    const userInfo = this.isBrowser ? this.ssrCoockieService.get(this.userInfoNameInLocalStorage) : this.request.cookies[this.userInfoNameInLocalStorage];
    return userInfo ? JSON.parse(userInfo) : null;
  }

  logout(): void {
    this.user = null;
    this.ssrCoockieService.deleteAll(this.tokenNameInLocalStorage);
    this.ssrCoockieService.deleteAll(this.userInfoNameInLocalStorage);
    if (this.isBrowser) {
      setTimeout(() => {
        window.location.href = '/';
      })
    }

  }

  checkToken(): CheckToken {
    return this.user?.accessToken ? CheckToken.VALID : CheckToken.INVALID;
  }

  checkTokenAndRedirectToAuth(): boolean {
    const isUser = this.checkToken() === CheckToken.VALID;
    if (!isUser && this.isBrowser) {
      if (!this.browserService.isMobileDevice) {
        this.dialog.open(LoginComponent, {
          width: '400px'
        });
      } else {
        this.router.navigate(['/login']);
      }
    }
    return isUser;
  }
}
